/** venture/kit13.ts — 14단계 「투자확인서 발급」 키트 상태 (저장소는 s13 그대로) (순수 함수, 2026-09-24)
 *  9단계 구비서류 카드와 같은 구조: 앞단계 연동값 → 자료수집 포털 제출값 → 직접 입력/업로드 순으로 채워진다.
 *  항목 정의는 kit13defs.ts · 준비도(readiness)와 화면(Step14Kit)이 같은 함수를 쓴다. VICS 보고는 13단계(vics13)가 따로 센다. */
import { getTxt, getUp, getStore, getChk } from './stepcommon';
import { getVers } from './gendocs';
import { getBondVers } from './bonddoc';
import { mval } from './collect';
import { kitKey } from './certplan';
import { certPlanOf, regCoOf, repPerson } from './coterms';
import { kitDefs } from './kit13defs';
import type { KitItem } from './kit13defs';
import { planModes, CERT_SHORT, isCapitalMode } from './types';
import type { Project, CoCard, CertMode, DocVer, Master, K13Result } from './types';
export { KBAN_URL } from './kit13defs';
export type { KitKind, KitFrom, KitItem } from './kit13defs';

export interface KitRowState { item: KitItem; linked: string; src: 'link' | 'manual'; own: string; done: boolean; res?: K13Result; }
const S13 = 's13' as const;

/** 방식별 항목 목록 (방식 미선택이면 빈 목록) */
export function kitItems(p: Project, co: CoCard, mode: CertMode | ''): KitItem[] {
  const plan = certPlanOf(regCoOf(p, co));
  return kitDefs(mode, plan.capital.rounds, co.id);
}

function lastReal(vers: DocVer[], signed?: boolean): string {
  const v = vers[vers.length - 1];
  if (!v || !(v.s3pdf || v.s3doc)) return '';
  if (signed && v.kind !== 'seal') return '';          // 서명(날인)본이어야 첨부할 수 있다
  return v.pdf || v.doc || '생성본';
}

const personLine = (x: { name: string; addr: string; rrn: string; tel: string }): string => [x.name || '', x.addr || '', x.rrn || '', x.tel || ''].join(' · ');
/** 마스터의 조합원 인적사항을 한 사람 한 줄로 */
export function membersText(m: Master | undefined): string {
  if (!m) return '';
  const rows = ['GP · ' + personLine(m.gp)].concat((m.lps || []).filter((l) => l.name).map((l) => 'LP · ' + personLine(l)));
  return rows.join('\n');
}
/** 이 법인의 대표(GP 또는 LP) 인적사항 — 자본금·대표자 재인수 방식의 투자자 기본값 */
export function repPersonText(p: Project, co: CoCard): string {
  const x = repPerson(p, co);
  return x ? personLine(x) : '';
}

/** 자료수집 포털(14단계) 제출 행 — 방식별 포털 ID 는 s13 txt cp_<coId>_<mode> */
export const portalKey = (coId: string, mode: CertMode | ''): string => 'cp_' + coId + '_' + (mode || 'none');
export const portalId = (p: Project, coId: string, mode: CertMode | ''): string => getTxt(p, S13, portalKey(coId, mode));
export function portalResult(p: Project, coId: string, mode: CertMode | '', title: string): K13Result | undefined {
  const cpid = portalId(p, coId, mode);
  const rows = cpid && p.k13res ? p.k13res[cpid] : undefined;
  return rows ? rows.find((r) => r.title === title && r.status !== 'none' && !!r.value) : undefined;
}

/** 항목 1개의 현재 상태 — 연동값(앞단계 → 자료수집 제출) · 직접값 · 완료 여부 */
export function kitRow(p: Project, co: CoCard, mode: CertMode | '', item: KitItem): KitRowState {
  const key = kitKey(co.id, mode, item.slot);
  const st = getStore(p, S13);
  let linked = '';
  const f = item.from;
  if (f) {
    if (f.kind === 'text' && f.store && f.key) linked = getTxt(p, f.store, f.key);
    else if (f.kind === 'file' && f.store && f.key) linked = getUp(p, f.store, f.key);
    else if (f.kind === 'doc') linked = f.bond ? lastReal(getBondVers(p, co, f.bond), f.signed) : lastReal(getVers(p, f.docKey || ''), f.signed);
    else if (f.kind === 'collect' && f.collectKey && p.collect) linked = mval(p.collect.gp, f.collectKey);
    else if (f.kind === 'master') linked = repPersonText(p, co);
  } else if (item.kind === 'members') {
    linked = membersText(p.master);
  }
  let res: K13Result | undefined;
  if (!linked && !item.tail) { res = portalResult(p, co.id, mode, item.label); if (res) linked = res.value; }
  const own = item.kind === 'file' ? getUp(p, S13, key) : getTxt(p, S13, key);
  const srcSaved = st.src && st.src[key];
  const hasAuto = !!f || item.kind === 'members' || !!res;
  // 생성 문서(계약서)는 서명본이 아직 없어도 「연동」이 기본 — 그 자리에서 생성·서명하도록
  const isDoc = !!(f && f.kind === 'doc');
  const src: 'link' | 'manual' = !hasAuto ? 'manual' : (srcSaved === 'manual' || srcSaved === 'link' ? srcSaved : (linked || isDoc ? 'link' : 'manual'));
  const done = src === 'link' ? !!linked : !!own.trim();
  return { item, linked, src, own, done, res };
}

export function kitRows(p: Project, co: CoCard, mode: CertMode | ''): KitRowState[] {
  return kitItems(p, co, mode).map((it) => kitRow(p, co, mode, it));
}
/** 자료수집으로 받을 항목 — 아직 못 채운 것만 (자동 연동·보관 항목 제외) */
export function kitMissing(p: Project, co: CoCard, mode: CertMode | ''): KitItem[] {
  return kitRows(p, co, mode).filter((r) => !r.done && !r.item.tail && r.item.kind !== 'members' && !(r.item.from && r.item.from.kind === 'doc')).map((r) => r.item);
}

/** 발급 완료 체크 키 — 방식별. 예전 단일 키(<coId>_invcert)는 첫 방식의 완료로 읽는다 */
export const invcertKey = (coId: string, mode: CertMode): string => kitKey(coId, mode, 'invcert');
export function invcertDone(p: Project, coId: string, mode: CertMode, primary: boolean): boolean {
  return getChk(p, S13, invcertKey(coId, mode)) || (primary && getChk(p, S13, coId + '_invcert'));
}

/** 준비도용 — [라벨, 완료] 목록 (방식 미선택 자체를 1건으로 센다 · 방식이 여럿이면 방식별로 묶는다) */
export function kitChecklist(p: Project, co: CoCard): [string, boolean][] {
  const nm = co.name || '법인';
  const plan = certPlanOf(regCoOf(p, co));
  const modes = planModes(plan);
  const out: [string, boolean][] = [[nm + '·발급 방식 선택', modes.length > 0]];
  if (modes.some(isCapitalMode)) {
    const ok = plan.capital.rounds.length > 0 && plan.capital.rounds.every((r) => r.date && r.amount && r.taxYear);
    out.push([nm + '·' + (modes.indexOf('capital_new') >= 0 && modes.indexOf('capital_inc') < 0 ? '설립 출자일·귀속연도' : '증자 회차·귀속연도'), ok]);
  }
  modes.forEach((mode, i) => {
    const tag = modes.length > 1 ? '[' + CERT_SHORT[mode] + '] ' : '';
    kitRows(p, co, mode).forEach((r) => out.push([nm + '·' + tag + r.item.label, r.done]));
    out.push([nm + '·' + tag + '투자확인서 발급', invcertDone(p, co.id, mode, i === 0)]);
  });
  return out;
}

/** 신청 정보 요약 — 발급시스템에 옮겨 적을 값 (방식 1개분) */
export function kitSummary(p: Project, co: CoCard, mode: CertMode): string {
  const rc = regCoOf(p, co);
  const plan = certPlanOf(rc);
  const regno = getTxt(p, 's8', '_regno');
  const issue = getTxt(p, 's8', co.id + '_b_issueDate');
  const modeKo: Record<CertMode, string> = { capital_inc: '자본금 증자', capital_new: '법인 설립 자본금', cb_fund: '전환사채 · 조합 인수', cb_direct: '전환사채 · 대표자 직접 재인수' };
  const lines: string[] = [
    '[투자확인서 신청 정보 · ' + modeKo[mode] + ']',
    '피투자기업: ' + (co.name || '') + (co.bizno ? ' (' + co.bizno + ')' : ''),
    '대표자: ' + ((rc && rc.ceo) || ''),
    '투자 방식: ' + modeKo[mode],
  ];
  if (isCapitalMode(mode)) {
    const tot = plan.capital.rounds.reduce((s, r) => s + (parseInt(String(r.amount).replace(/[^0-9]/g, ''), 10) || 0), 0);
    lines.push('투자금액: ' + (tot ? tot.toLocaleString('ko-KR') + '원' : '(회차 금액 미입력)'));
    plan.capital.rounds.forEach((r, i) => lines.push((mode === 'capital_new' ? '설립 출자' : (i + 1) + '회차 증자') + ': ' + (r.date || '-') + ' · ' + (r.amount ? r.amount + '원' : '-') + ' · 귀속 ' + (r.taxYear ? r.taxYear + '년' : '-')));
    const inv = kitRow(p, co, mode, kitItems(p, co, mode).filter((x) => x.slot === 'investors')[0]);
    if (inv && (inv.src === 'link' ? inv.linked : inv.own)) lines.push('투자자:', inv.src === 'link' ? inv.linked : inv.own);
  } else {
    lines.push('투자금액: ' + ((rc && rc.amt) ? rc.amt + '원' : '(등록정보 미입력)'));
    lines.push('조합명: ' + (p.org || ''), '조합 등록번호: ' + (regno || '(8단계 미입력)'), '사채 발행일: ' + (issue || '(8단계 미입력)'));
    if (mode === 'cb_fund') { const mt = membersText(p.master); if (mt) lines.push('조합원:', mt); }
    if (mode === 'cb_direct') lines.push('재인수자: ' + (plan.direct.buyer || (rc && rc.ceo) || '-') + ' · ' + (plan.direct.when || '-'));
  }
  return lines.join('\n');
}
